// FILE: stratify_website/src/pages/SupportPage.jsx
import React from "react";
import { SITE } from "../config/site";
import { Meta } from "../layout/EducationMeta";
import GlassCard from "../components/ui/GlassCard";
import Link from "../components/Link";
import { LEGAL_RELATED_LINKS, LEGAL_SHARED } from "../config/legal";

const PAGE =
  "mx-auto w-full max-w-[110rem] px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12";

const quickHelp = [
  {
    title: "Account or login help",
    body:
      "Website and iPhone sign-in use Google. For issues signing in or deleting your account, recover access through Google account recovery, then email support with the Google address connected to Stratify.",
  },
  {
    title: "Backtesting or strategy result questions",
    body:
      "Include the ticker, timeframe, strategy name, and what looks wrong so we can review the result, calculation, or chart behavior faster.",
  },
  {
    title: "Notifications and alerts",
    body:
      "If strategy alerts are delayed or not arriving, check iOS notification permissions first, then contact support with your device model and app version.",
  },
  {
    title: "Billing or subscription help",
    body:
      "Website subscriptions are managed from the pricing page after you sign in, once website billing is available. Apple-billed subscriptions are managed in iOS Settings → Apple ID → Subscriptions. Cancellation normally stops renewal at the end of the paid period. Cancellation and a refund are separate.",
  },
  {
    title: "Refund request",
    body:
      "Use the subject “Refund Request”. Include your account email, invoice or order reference if you have one, purchase source (Stripe or Apple), and the reason. See the Refund Policy.",
  },
];

const requestChecklist = [
  "Your name and the email attached to your Stratify account",
  "Your device model and iOS version, if the issue is technical",
  "The app version you are using, if available",
  "Screenshots or a short screen recording, if it helps show the issue",
  "For strategy issues: ticker, timeframe, strategy name, and what result you expected",
];

export default function SupportPage() {
  const canonical = SITE.supportUrl;

  const supportSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Stratify Backtesting Support",
    url: canonical,
    description:
      "Support page for Stratify Backtesting account help, app issues, privacy requests, and trading strategy simulation questions.",
    mainEntity: {
      "@type": "Organization",
      name: SITE.legalName,
      url: SITE.url,
      email: SITE.supportEmail,
      contactPoint: {
        "@type": "ContactPoint",
        email: SITE.supportEmail,
        contactType: "customer support",
        availableLanguage: ["English"],
      },
    },
  };

  return (
    <div className={`${PAGE} py-12 md:py-16`}>
      <Meta
        title="Support — Stratify Backtesting"
        description="Contact Stratify Backtesting support for account help, app issues, notifications, privacy requests, and strategy simulation questions."
        canonical={canonical}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(supportSchema) }}
      />

      <section className="max-w-4xl">
        <div className="inline-flex items-center rounded-full border border-hint/25 bg-hint/10 px-3 py-1 text-xs font-semibold tracking-wide text-hint-deep">
          Official support page
        </div>

        <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight text-ink">
          Stratify Support
        </h1>

        <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-muted">
          Need help with Stratify Backtesting? Contact us for account access,
          technical issues, strategy monitoring, app feedback, privacy requests,
          website billing, and support with the iOS app.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted">
          Do not email passwords, one-time codes, card numbers, bank details, or API keys.
          {` ${LEGAL_SHARED.noLiveTrading}`}
        </p>
      </section>

      <section className="mt-10 grid gap-5 lg:grid-cols-3">
        <GlassCard className="p-6 lg:col-span-2">
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-muted">
            Contact
          </div>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight text-ink">
            Email support
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-ink/66">
            For the fastest help, email us with a clear subject line and include
            the details listed below. We currently provide support in English.
          </p>

          <div className="mt-6 rounded-2xl border border-ink/8 bg-bg1 p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
              Support email
            </div>
            <a
              href={`mailto:${SITE.supportEmail}`}
              className="mt-2 inline-flex break-all text-lg font-semibold text-hint-deep hover:text-ink transition"
            >
              {SITE.supportEmail}
            </a>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <a
              href={`mailto:${SITE.supportEmail}?subject=Stratify%20Support%20Request`}
              className="inline-flex items-center justify-center rounded-xl border border-hint/25 bg-hint/15 px-4 py-3 text-sm font-semibold text-ink hover:bg-hint/25 transition"
            >
              Email support →
            </a>
            <Link
              to="/privacy-policy"
              className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
            >
              View privacy policy →
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
            >
              Pricing and billing portal →
            </Link>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="text-sm font-semibold uppercase tracking-[0.18em] text-muted">
            App details
          </div>
          <dl className="mt-4 space-y-4 text-sm">
            <div>
              <dt className="text-muted">App</dt>
              <dd className="mt-1 font-semibold text-ink">{SITE.appName}</dd>
            </div>
            <div>
              <dt className="text-muted">Company</dt>
              <dd className="mt-1 font-semibold text-ink">{SITE.legalName}</dd>
            </div>
            <div>
              <dt className="text-muted">Website</dt>
              <dd className="mt-1">
                <a href={SITE.url} className="font-semibold text-hint-deep hover:text-ink transition">
                  {SITE.url.replace("https://", "")}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-muted">Privacy</dt>
              <dd className="mt-1">
                <Link to="/privacy-policy" className="font-semibold text-hint-deep hover:text-ink transition">
                  Privacy Policy
                </Link>
              </dd>
            </div>
          </dl>
        </GlassCard>
      </section>

      <section className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {quickHelp.map((item) => (
          <GlassCard key={item.title} className="p-5">
            <h2 className="text-base font-semibold text-ink">{item.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted">{item.body}</p>
          </GlassCard>
        ))}
      </section>

      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <h2 className="text-xl font-semibold tracking-tight text-ink">
            What to include in your support request
          </h2>
          <ul className="mt-4 space-y-3">
            {requestChecklist.map((item) => (
              <li key={item} className="flex gap-3 text-sm leading-relaxed text-ink/66">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-hint-strong" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </GlassCard>

        <GlassCard className="p-6">
          <h2 className="text-xl font-semibold tracking-tight text-ink">
            Data deletion and privacy requests
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-ink/66">
            You can request access, correction, or deletion of personal information
            associated with your Stratify account by emailing support. For deletion
            requests, include the email address connected to your account so we can
            identify the correct record.
          </p>
          <div className="mt-5 rounded-2xl border border-ink/8 bg-bg1 p-4 text-sm text-muted">
            Stratify is an educational and simulation tool. It is not a brokerage,
            trading platform, investment advisor, or financial advisor.
          </div>
        </GlassCard>
      </section>

      <section className="mt-8 flex flex-wrap gap-3">
        {LEGAL_RELATED_LINKS.filter((item) => item.to !== "/support").map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
          >
            {item.label} →
          </Link>
        ))}
      </section>
    </div>
  );
}
